import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save } from "lucide-react";
import api from "../../../../utils/api";
import PageFormHeader from "../components/PageFormHeader";
import ExcavationManualForm from "./components/ExcavationManualForm";
import { useToast } from "../components/AdminSharedComponents";

const EditExcavation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    project_id: "",
    coordinates: null,
    depth: "",
    status: "Planned",
  });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Prevent loading excavation twice
  const hasLoadedExcavation = useRef(false);

  // Fetch projects and excavation data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Always fetch projects
        const projectsResponse = await api.get("/api/projects");
        const projectsData =
          projectsResponse.data.data || projectsResponse.data || [];
        setProjects(projectsData);

        if (id && !hasLoadedExcavation.current) {
          const excavationResponse = await api.get(
            `/api/excavation-points/${id}`
          );
          const excavation =
            excavationResponse.data.data || excavationResponse.data;

          setFormData({
            name: excavation.name || "",
            description: excavation.description || "",
            project_id: excavation.project_id || "",
            coordinates: excavation.coordinates || null,
            depth: excavation.depth || "",
            status: excavation.status || "Planned",
          });

          hasLoadedExcavation.current = true;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Handle coordinate changes
  const handleCoordinateChange = (coordinates) => {
    setFormData((prev) => ({
      ...prev,
      coordinates,
    }));
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Excavation name is required";
    }

    if (!formData.project_id) {
      errors.project_id = "Project is required";
    }

    if (!formData.coordinates) {
      errors.coordinates = "Location coordinates are required";
    }

    if (formData.depth && isNaN(parseFloat(formData.depth))) {
      errors.depth = "Depth must be a valid number";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        project_id: Number(formData.project_id),
        coordinates: formData.coordinates,
        depth: formData.depth ? parseFloat(formData.depth) : null,
        status: formData.status,
      };

      await api.put(`/api/excavation-points/${id}`, submitData);
      toast.success("Excavation point updated successfully");
      navigate("/admin/projects/excavation/map");
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to save excavation point";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex justify-center items-center z-[9999] bg-gray-50">
        <div className="animate-pulse text-lg font-semibold text-primary">
          Loading excavation point...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageFormHeader
        title="Edit Excavation Point"
        backLink="/admin/projects/excavation/map"
        backText="Back to Map"
        cancelLink="/admin/projects/excavation/map"
        cancelText="Cancel"
        submitText={submitting ? "Updating..." : "Update Point"}
        loadingText="Updating..."
        loading={submitting}
        onSubmit={handleSubmit}
        submitIcon={Save}
        formId="excavation-edit-form"
      />

      {/* Main Content */}
      <div className="px-4 md:px-6 py-6 md:py-8 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto">
          <ExcavationManualForm
            formData={formData}
            projects={projects}
            validationErrors={validationErrors}
            handleChange={handleChange}
            handleCoordinateChange={handleCoordinateChange}
            submitting={submitting}
          />
        </div>
      </div>
    </div>
  );
};

export default EditExcavation;
